// api
import { transformChannelGroupWrapper } from '~/src/dash/api/channel-group';
import { transformChannelListWrapper } from '~/src/dash/api/channel-list';
import {
  EAllowStatus,
  IAdserverCampaignTracker,
  IAdserverCreative,
  IAdserverCreativeFlightDatesSubgroup,
  IChannelGroup,
  IChannelGroupAdserverCampaignAssociation,
  IChannelList,
  IChannelListAdserverCampaignAssociation,
  ICountryGroup,
  ISite,
  ISiteAdserverCampaignAssociation,
  marshalAdserverCampaignTrackerWrapper,
  marshalAdserverCreativeWrapper,
  TargetBoolType,
  TargetingCCPA,
  TargetingCoppa,
  ETargetingDeviceIDTypes,
  TargetingGDPR,
  TargetingLogic,
  transformAdserverCampaignTrackerWrapper,
  transformAdserverCreativeWrapper,
  ExtendedIDTargetingType,
} from '~/src/dash/api/console-api';
import { transformCountryGroup } from '~/src/dash/api/country-group';
import { ECurrencyType } from '~/src/dash/api/exchange-rates';
import { IFrequencyCap, transformFrequencyCap } from '~/src/dash/api/frequency-cap';
import { IAdserverOrder } from '~/src/dash/api/order';
import {
  parseArray,
  parseBoolTrue,
  parseFloat,
  parseNumber,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import { EBool } from '~/src/dash/api/types/bool';
// utils
import { ECampaignStatus } from '~/src/dash/utils/campaign-status';
import { ELogicalOperator } from '~/src/dash/utils/options';
import { TargetingPresetParamKey } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/config';

export type CampaignActive = string;
export const CampaignActiveInactive: CampaignActive = '1';
export const CampaignActiveActive: CampaignActive = '2';

export const transformSiteAdserverCampaignAssociations = transformModel({
  ID: ['ID'],
  SiteID: ['SiteID'],
  AdserverCampaignID: ['AdserverCampaignID'],
  SiteWeight: ['SiteWeight'],
});

export const transformChannelGroupAdserverCampaignAssociations = transformModel({
  ID: ['ID'],
  ChannelGroupID: ['ChannelGroupID'],
  AdserverCampaignID: ['AdserverCampaignID'],
  SiteWeight: ['SiteWeight'],
});

export const transformChannelListAdserverCampaignAssociations = transformModel({
  ID: ['ID'],
  ChannelListID: ['ChannelListID'],
  AdserverCampaignID: ['AdserverCampaignID'],
});

export interface IFlightDateCheckpoint {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  AdserverCampaignID: number;
  StartDate: string;
  EndDate: string;
  Quantity: number;
  CRONQuantityCount?: number;
  Name: string;
  CheckpointDurationInHours?: number;

  // ui only fields
  errors: string; // holds error messages for checkpoint dates
}

export const transformFlightDateCheckpoint = transformModel({
  ID: parseNumber(['ID']),
  CreatedAt: parseString(['CreatedAt']),
  UpdatedAt: parseString(['UpdatedAt']),
  DeletedAt: parseString(['DeletedAt']),

  AdserverCampaignID: parseNumber(['AdserverCampaignID']),
  StartDate: parseString(['StartDate']),
  EndDate: parseString(['EndDate']),
  Quantity: parseNumber(['Quantity']),
  CRONQuantityCount: parseNumber(['CRONQuantityCount']),
  Name: parseString(['Name']),
  CheckpointDurationInHours: parseNumber(['CheckpointDurationInHours']),
});

export interface IAdserverCampaign {
  ID: string;
  UUID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  StartDate: string;
  EndDate: string;
  Timezone: string;
  PublisherID: string;
  Name: string;
  Active: CampaignActive;
  AdserverCampaignTrackers: IAdserverCampaignTracker[];
  AdserverCreatives: IAdserverCreative[];
  AdserverCreativeFlightDatesSubgroups: IAdserverCreativeFlightDatesSubgroup[];
  CPM: string;
  GrossCPM: string;
  CurrencyDisplayed: ECurrencyType;
  Adomain: string;
  Agency: string;
  Sites: string[];
  CampaignType: string;

  FrequencyActive: string;
  FrequencyTTL: string;
  FrequencyLimit: string;
  FrequencyLimitDays: string;
  FrequencyLimitHours: string;

  Quantity: string;

  IABCategory: string;
  TargetedCountries: string;
  TargetingDevices: string;
  TargetingDevicesIsAllowed: EAllowStatus;
  TargetingAudienceSegmentID: string;
  TargetingAudienceSegmentIDIsAllowed: EAllowStatus;
  TargetingSitePages: string;
  TargetingSitePagesIsAllowed: EAllowStatus;
  TargetingLivestream: number;
  LocalTargetingAllowed: string;
  LocalTargetingDisallowed: string;
  TargetingContentChannel: string;
  TargetingContentChannelIsAllowed: EAllowStatus;
  TargetingContentDistributor: string;
  TargetingContentDistributorIsAllowed: EAllowStatus;
  TargetingContentGenre: string;
  TargetingContentGenreIsAllowed: EAllowStatus;
  TargetingContentID: string;
  TargetingContentIDIsAllowed: EAllowStatus;
  TargetingContentNetwork: string;
  TargetingContentNetworkIsAllowed: EAllowStatus;
  TargetingContentProducer: string;
  TargetingContentProducerIsAllowed: EAllowStatus;
  TargetingContentRating: string;
  TargetingContentRatingIsAllowed: EAllowStatus;
  TargetingContentSeries: string;
  TargetingContentSeriesIsAllowed: EAllowStatus;
  TargetingContentTitle: string;
  TargetingContentTitleIsAllowed: EAllowStatus;
  TargetingContentLanguage: string;
  TargetingContentLanguageIsAllowed: EAllowStatus;
  TargetingCountryGroupIDs: string;
  TargetingCountryGroupIDsIsAllowed: EAllowStatus;
  TargetingAppBundleID: string;
  TargetingAppBundleIDIsAllowed: EAllowStatus;
  TargetingAppBundleSitePagesLogic: ELogicalOperator;
  TargetingDealID: string;
  TargetingDealIDIsAllowed: EAllowStatus;
  TargetingFWSectionIDs: string;
  TargetingFWSectionIDsIsAllowed: EAllowStatus;
  TargetingDeviceTypes: string;
  TargetingDeviceTypesIsAllowed: EAllowStatus;
  TargetingDeviceSubTypes: string;
  TargetingDeviceSubTypesIsAllowed: EAllowStatus;
  TargetingDeviceModels: string;
  TargetingDeviceModelsIsAllowed: EAllowStatus;
  TargetingCoppa: TargetingCoppa;
  TargetingLimitedAdTracking: TargetBoolType;
  CustomTargeting: string;
  CustomTargetingInclusion: EAllowStatus;
  TargetingCustom4: string;
  TargetingCustom4Inclusion: EAllowStatus;
  TargetingCustom5: string;
  TargetingCustom5Inclusion: EAllowStatus;
  TargetingCustom6: string;
  TargetingCustom6Inclusion: EAllowStatus;
  TargetingCustom7: string;
  TargetingCustom7Inclusion: EAllowStatus;
  TargetingCustom8: string;
  TargetingCustom8Inclusion: EAllowStatus;
  TargetingCustom9: string;
  TargetingCustom9Inclusion: EAllowStatus;
  TargetingCustom10: string;
  TargetingCustom10Inclusion: EAllowStatus;
  TargetingCustom11: string;
  TargetingCustom11Inclusion: EAllowStatus;
  TargetingCustom12: string;
  TargetingCustom12Inclusion: EAllowStatus;
  TargetingCustom13: string;
  TargetingCustom13Inclusion: EAllowStatus;
  TargetingCustom14: string;
  TargetingCustom14Inclusion: EAllowStatus;
  TargetingCustom15: string;
  TargetingCustom15Inclusion: EAllowStatus;
  TargetingCustom16: string;
  TargetingCustom16Inclusion: EAllowStatus;
  TargetingCustom17: string;
  TargetingCustom17Inclusion: EAllowStatus;
  TargetingCustom18: string;
  TargetingCustom18Inclusion: EAllowStatus;
  IPFilter: string;
  PacingType: number;
  Dayparting: string;
  DaypartingIsAllowed: EAllowStatus;
  CRONQuantityCount: string;
  isCampaignTargeted: boolean;
  SegmentTargeting: number;
  ChannelGroupIDS: number[];
  ContextualSegmentIDS: number[];
  ChannelGroups: IChannelGroup[];
  ChannelLists: IChannelList[];
  ChannelListIDS: number[];
  ChannelListAdserverCampaignAssociations: IChannelListAdserverCampaignAssociation[];
  CountryGroups: ICountryGroup[];
  Pacing?: number;
  Status?: ECampaignStatus;
  AdserverOrderID: number;
  AdserverOrder: IAdserverOrder;
  IsTargetedByHB: boolean;
  SiteAdserverCampaignAssociations: ISiteAdserverCampaignAssociation[];
  ChannelGroupAdserverCampaignAssociations: IChannelGroupAdserverCampaignAssociation[];
  SiteWeightsActive: EBool;
  DisableLowPacing: EBool;
  EnableOverDelivery: EBool;
  FrequencyCaps: IFrequencyCap[];
  SkipEnabled: EBool;
  SkipOffset: number;
  LocalTargetingAllowedLogic: TargetingLogic;
  LocalTargetingDisallowedLogic: TargetingLogic;
  FlightDateCheckpoints: IFlightDateCheckpoint[];
  FlightDateCheckpointsEnabled: EBool;
  FlightDateCheckpointsRolloverEnabled: EBool;
  GuaranteedDeliveryEnabled: EBool;
  Alias: string;
  TargetingCCPA: TargetingCCPA;
  EnforceSameCreativePerReqEnabled: EBool;
  EnableFlightDateSubgroups: EBool;
  CategoryExclusivityEnabled: EBool;
  TargetingRequestedPodDurationMin: number;
  TargetingRequestedPodDurationMax: number;
  TargetingGvinParamExists: EBool;
  CampaignDurationInHours: number;
  ServeRightAway: EBool;
  TargetingGDPR: TargetingGDPR;
  TargetingGDPRConsent: TargetBoolType;
  PresetIDs: number[];
  presetConflicts: Record<string, TargetingPresetParamKey[]>;
  presetConflictedParamKeys: TargetingPresetParamKey[];
  SamplingPercentageDisabledUntil: string;
  SamplingPercentage: number;
  TargetingDeviceIDType: ETargetingDeviceIDTypes;
  // ExtendedIDTargetingType
  TargetingUID20TargetingType: ExtendedIDTargetingType;
  TargetingGooglePairIDTargetingType: ExtendedIDTargetingType;
  TargetingLiveRampATSTargetingType: ExtendedIDTargetingType;
  TargetingIrisSignalTargetingType: ExtendedIDTargetingType;
  // TargetingSamsungIdentityTargetingType: ExtendedIDTargetingType;
  // TargetingSamsungEPGMetadataTargetingType: ExtendedIDTargetingType;
}

export const transformCampaign = transformModel<IAdserverCampaign>({
  ID: parseString(['ID']),
  UUID: ['UUID'],
  CreatedAt: ['CreatedAt'],
  UpdatedAt: ['UpdatedAt'],
  DeletedAt: ['DeletedAt'],

  PublisherID: parseString(['PublisherID']),
  Name: parseString(['Name']),
  Active: parseString(['Active']),
  StartDate: parseString(['StartDate']),
  EndDate: parseString(['EndDate']),
  Timezone: parseString(['Timezone']),
  AdserverCampaignTrackers: parseArray(
    ['AdserverCampaignTrackers'],
    transformAdserverCampaignTrackerWrapper,
  ),
  AdserverCreatives: parseArray(['AdserverCreatives'], transformAdserverCreativeWrapper),
  CPM: parseString(['CPM']),
  GrossCPM: parseString(['GrossCPM']),
  CurrencyDisplayed: parseNumber(['CurrencyDisplayed', 'currency_displayed']),
  Adomain: parseString(['Adomain']),
  Agency: parseString(['Agency']),
  Sites: parseArray(['Sites'], (v: number | string | ISite) => {
    if (typeof v === 'object') {
      return '' + v.ID;
    }
    return '' + v;
  }),
  CampaignType: parseString(['CampaignType']),
  FrequencyActive: parseString(['FrequencyActive']),
  FrequencyTTL: parseString(['FrequencyTTL']),
  FrequencyLimit: parseString(['FrequencyLimit']),
  FrequencyLimitDays: parseString(['FrequencyLimitDays']),
  FrequencyLimitHours: parseString(['FrequencyLimitHours']),
  Quantity: parseString(['Quantity']),
  IABCategory: parseString(['IABCategory']),
  CustomTargeting: parseString(['CustomTargeting']),
  CustomTargetingInclusion: parseNumber(['CustomTargetingInclusion']),
  TargetingCustom4: parseString(['TargetingCustom4']),
  TargetingCustom4Inclusion: parseNumber(['TargetingCustom4Inclusion']),
  TargetingCustom5: parseString(['TargetingCustom5']),
  TargetingCustom5Inclusion: parseNumber(['TargetingCustom5Inclusion']),
  TargetingCustom6: parseString(['TargetingCustom6']),
  TargetingCustom6Inclusion: parseNumber(['TargetingCustom6Inclusion']),
  TargetingCustom7: parseString(['TargetingCustom7']),
  TargetingCustom7Inclusion: parseNumber(['TargetingCustom7Inclusion']),
  TargetingCustom8: parseString(['TargetingCustom8']),
  TargetingCustom8Inclusion: parseNumber(['TargetingCustom8Inclusion']),
  TargetingCustom9: parseString(['TargetingCustom9']),
  TargetingCustom9Inclusion: parseNumber(['TargetingCustom9Inclusion']),
  TargetingCustom10: parseString(['TargetingCustom10']),
  TargetingCustom10Inclusion: parseNumber(['TargetingCustom10Inclusion']),
  TargetingCustom11: parseString(['TargetingCustom11']),
  TargetingCustom11Inclusion: parseNumber(['TargetingCustom11Inclusion']),
  TargetingCustom12: parseString(['TargetingCustom12']),
  TargetingCustom12Inclusion: parseNumber(['TargetingCustom12Inclusion']),
  TargetingCustom13: parseString(['TargetingCustom13']),
  TargetingCustom13Inclusion: parseNumber(['TargetingCustom13Inclusion']),
  TargetingCustom14: parseString(['TargetingCustom14']),
  TargetingCustom14Inclusion: parseNumber(['TargetingCustom14Inclusion']),
  TargetingCustom15: parseString(['TargetingCustom15']),
  TargetingCustom15Inclusion: parseNumber(['TargetingCustom15Inclusion']),
  TargetingCustom16: parseString(['TargetingCustom16']),
  TargetingCustom16Inclusion: parseNumber(['TargetingCustom16Inclusion']),
  TargetingCustom17: parseString(['TargetingCustom17']),
  TargetingCustom17Inclusion: parseNumber(['TargetingCustom17Inclusion']),
  TargetingCustom18: parseString(['TargetingCustom18']),
  TargetingCustom18Inclusion: parseNumber(['TargetingCustom18Inclusion']),
  LocalTargetingAllowed: parseString(['LocalTargetingAllowed']),
  LocalTargetingDisallowed: parseString(['LocalTargetingDisallowed']),
  SegmentTargeting: parseNumber(['SegmentTargeting']),
  TargetedCountries: parseString(['TargetedCountries']),
  TargetingDevices: parseString(['TargetingDevices']),
  TargetingDevicesIsAllowed: parseNumber('TargetingDevicesIsAllowed'),
  TargetingDeviceSubTypes: parseString(['TargetingDeviceSubTypes']),
  TargetingDeviceSubTypesIsAllowed: parseNumber('TargetingDeviceSubTypesIsAllowed'),
  TargetingDeviceModels: parseString(['TargetingDeviceModels']),
  TargetingDeviceModelsIsAllowed: parseNumber('TargetingDeviceModelsIsAllowed'),
  TargetingDeviceTypes: parseString(['TargetingDeviceTypes']),
  TargetingDeviceTypesIsAllowed: parseNumber(['TargetingDeviceTypesIsAllowed']),
  TargetingCoppa: parseNumber(['TargetingCoppa']),
  TargetingLimitedAdTracking: parseNumber(['TargetingLimitedAdTracking']),
  TargetingAudienceSegmentID: parseString(['TargetingAudienceSegmentID']),
  TargetingSitePages: parseString(['TargetingSitePages']),
  TargetingSitePagesIsAllowed: parseNumber(['TargetingSitePagesIsAllowed']),
  TargetingContentChannel: parseString(['TargetingContentChannel']),
  TargetingContentChannelIsAllowed: parseNumber('TargetingContentChannelIsAllowed'),
  TargetingContentDistributor: parseString(['TargetingContentDistributor']),
  TargetingContentDistributorIsAllowed: parseNumber(['TargetingContentDistributorIsAllowed']),
  TargetingContentGenre: parseString(['TargetingContentGenre']),
  TargetingContentGenreIsAllowed: parseNumber(['TargetingContentGenreIsAllowed']),
  TargetingContentID: parseString(['TargetingContentID']),
  TargetingContentIDIsAllowed: parseNumber('TargetingContentIDIsAllowed'),
  TargetingContentNetwork: parseString(['TargetingContentNetwork']),
  TargetingContentNetworkIsAllowed: parseNumber('TargetingContentNetworkIsAllowed'),
  TargetingContentProducer: parseString(['TargetingContentProducer']),
  TargetingContentProducerIsAllowed: parseNumber(['TargetingContentProducerIsAllowed']),
  TargetingContentRating: parseString(['TargetingContentRating']),
  TargetingContentRatingIsAllowed: parseNumber(['TargetingContentRatingIsAllowed']),
  TargetingContentSeries: parseString(['TargetingContentSeries']),
  TargetingContentSeriesIsAllowed: parseNumber(['TargetingContentSeriesIsAllowed']),
  TargetingContentTitle: parseString(['TargetingContentTitle']),
  TargetingContentTitleIsAllowed: parseNumber(['TargetingContentTitleIsAllowed']),
  TargetingContentLanguage: parseString(['TargetingContentLanguage']),
  TargetingContentLanguageIsAllowed: parseNumber(['TargetingContentLanguageIsAllowed']),
  TargetingLivestream: parseNumber(['TargetingLivestream']),
  TargetingAudienceSegmentIDIsAllowed: parseNumber('TargetingAudienceSegmentIDIsAllowed'),
  TargetingAppBundleID: parseString(['TargetingAppBundleID']),
  TargetingAppBundleIDIsAllowed: parseNumber('TargetingAppBundleIDIsAllowed'),
  TargetingAppBundleSitePagesLogic: parseNumber('TargetingAppBundleSitePagesLogic'),
  TargetingDealID: parseString(['TargetingDealID']),
  TargetingDealIDIsAllowed: parseNumber('TargetingDealIDIsAllowed'),
  TargetingFWSectionIDs: parseString(['TargetingFWSectionIDs']),
  TargetingFWSectionIDsIsAllowed: parseNumber('TargetingFWSectionIDsIsAllowed'),
  TargetingCountryGroupIDs: parseString(['TargetingCountryGroupIDs']),
  TargetingCountryGroupIDsIsAllowed: parseNumber('TargetingCountryGroupIDsIsAllowed'),
  TargetingRequestedPodDurationMin: ['TargetingRequestedPodDurationMin'],
  TargetingRequestedPodDurationMax: ['TargetingRequestedPodDurationMax'],
  IPFilter: parseString(['IPFilter']),
  PacingType: parseString(['PacingType']),
  Dayparting: parseString(['Dayparting']),
  DaypartingIsAllowed: parseNumber(['DaypartingIsAllowed']),
  CRONQuantityCount: parseString(['CRONQuantityCount']),
  ChannelGroupIDS: parseArray(['ChannelGroupIDS', 'channel_group_ids'], (obj: any) => {
    return obj;
  }),
  ChannelGroups: parseArray(['ChannelGroups', 'channelgroups'], transformChannelGroupWrapper),
  ChannelListIDS: parseArray(['ChannelListIDS', 'channel_list_ids'], (obj: any) => {
    return obj;
  }),
  ChannelLists: parseArray(['ChannelLists', 'channellists'], transformChannelListWrapper),
  ChannelListAdserverCampaignAssociations: parseArray(
    ['ChannelListToAdserverCampaignAssociations'],
    transformChannelListAdserverCampaignAssociations,
  ),
  CountryGroups: parseArray(['CountryGroups'], transformCountryGroup),
  ContextualSegmentIDS: parseArray(['ContextualSegments'], (obj: any) => {
    return obj.ID;
  }),
  AdserverOrderID: parseNumber(['AdserverOrderID']),
  IsTargetedByHB: parseBoolTrue(['IsTargetedByHB']),
  SiteAdserverCampaignAssociations: parseArray(
    ['SiteAdserverCampaignAssociations'],
    transformSiteAdserverCampaignAssociations,
  ),
  ChannelGroupAdserverCampaignAssociations: parseArray(
    ['ChannelGroupToAdserverCampaignAssociations'],
    transformChannelGroupAdserverCampaignAssociations,
  ),
  SiteWeightsActive: parseNumber(['SiteWeightsActive']),
  EnableOverDelivery: parseNumber(['EnableOverDelivery']),
  FrequencyCaps: parseArray(['FrequencyCaps'], transformFrequencyCap),
  DisableLowPacing: parseNumber(['DisableLowPacing']),
  SkipEnabled: parseNumber(['SkipEnabled']),
  SkipOffset: parseNumber(['SkipOffset']),
  LocalTargetingAllowedLogic: parseNumber(['LocalTargetingAllowedLogic']),
  LocalTargetingDisallowedLogic: parseNumber(['LocalTargetingDisallowedLogic']),
  FlightDateCheckpoints: parseArray(['FlightDateCheckpoints'], transformFlightDateCheckpoint),
  FlightDateCheckpointsEnabled: parseNumber(['FlightDateCheckpointsEnabled']),
  FlightDateCheckpointsRolloverEnabled: parseNumber(['FlightDateCheckpointsRolloverEnabled']),
  GuaranteedDeliveryEnabled: parseNumber(['GuaranteedDeliveryEnabled']),
  Alias: parseString(['Alias']),
  TargetingCCPA: ['TargetingCCPA'],
  EnforceSameCreativePerReqEnabled: ['EnforceSameCreativePerReqEnabled'],
  EnableFlightDateSubgroups: ['EnableFlightDateSubgroups'],
  CategoryExclusivityEnabled: ['CategoryExclusivityEnabled'],
  TargetingGvinParamExists: ['TargetingGvinParamExists'],
  CampaignDurationInHours: parseNumber(['CampaignDurationInHours']),
  ServeRightAway: parseNumber(['ServeRightAway']),
  TargetingGDPR: parseNumber(['TargetingGDPR', 'targeting_gdpr']),
  TargetingGDPRConsent: parseNumber(['TargetingGDPRConsent', 'targeting_gdpr_consent']),
  PresetIDs: ['PresetIDs', 'preset_ids'],
  SamplingPercentageDisabledUntil: ['SamplingPercentageDisabledUntil'],
  SamplingPercentage: parseFloat(['SamplingPercentage']),
  TargetingDeviceIDType: parseNumber(['TargetingDeviceIDType']),
  TargetingUID20TargetingType: parseNumber(['TargetingUID20TargetingType']),
  TargetingGooglePairIDTargetingType: parseNumber(['TargetingGooglePairIDTargetingType']),
  TargetingLiveRampATSTargetingType: parseNumber(['TargetingLiveRampATSTargetingType']),
  TargetingIrisSignalTargetingType: parseNumber(['TargetingIrisSignalTargetingType']),
  // TargetingSamsungIdentityTargetingType: parseNumber(['TargetingSamsungIdentityTargetingType']),
  // TargetingSamsungEPGMetadataTargetingType: parseNumber([
  //   'TargetingSamsungEPGMetadataTargetingType',
  // ]),
});

export function transformCampaignWrapper(fromObj: any): IAdserverCampaign {
  return transformCampaign(fromObj);
}

export const marshalAdserverCampaign = transformModel({
  ID: ['ID'],
  UUID: ['UUID'],

  Name: ['Name'],
  PublisherID: ['PublisherID'],
  StartDate: ['StartDate'],
  EndDate: ['EndDate'],
  Timezone: ['Timezone'],
  CPM: ['CPM'],
  GrossCPM: ['GrossCPM'],
  CurrencyDisplayed: parseNumber(['CurrencyDisplayed', 'currency_displayed']),
  Adomain: ['Adomain'],
  Agency: parseString(['Agency']),
  Active: ['Active'],
  AdserverCampaignTrackers: parseArray(
    ['AdserverCampaignTrackers'],
    marshalAdserverCampaignTrackerWrapper,
  ),
  AdserverCreatives: parseArray(['AdserverCreatives'], marshalAdserverCreativeWrapper),
  Sites: ['Sites'],
  CampaignType: ['CampaignType'],
  FrequencyActive: parseString(['FrequencyActive']),
  FrequencyTTL: parseString(['FrequencyTTL']),
  FrequencyLimit: parseString(['FrequencyLimit']),
  FrequencyLimitDays: parseString(['FrequencyLimitDays']),
  FrequencyLimitHours: parseString(['FrequencyLimitHours']),
  FrequencyCaps: parseArray(['FrequencyCaps'], transformFrequencyCap),
  Quantity: parseString(['Quantity']),
  IABCategory: parseString(['IABCategory']),
  TargetedCountries: ['TargetedCountries'],
  TargetingDevices: parseString(['TargetingDevices']),
  TargetingDevicesIsAllowed: parseNumber('TargetingDevicesIsAllowed'),
  TargetingAudienceSegmentID: parseString(['TargetingAudienceSegmentID']),
  TargetingSitePages: parseString(['TargetingSitePages']),
  TargetingSitePagesIsAllowed: parseNumber(['TargetingSitePagesIsAllowed']),
  TargetingContentChannel: parseString(['TargetingContentChannel']),
  TargetingContentChannelIsAllowed: parseNumber('TargetingContentChannelIsAllowed'),
  TargetingContentDistributor: parseString(['TargetingContentDistributor']),
  TargetingContentDistributorIsAllowed: parseNumber(['TargetingContentDistributorIsAllowed']),
  TargetingContentGenre: parseString(['TargetingContentGenre']),
  TargetingContentGenreIsAllowed: parseNumber(['TargetingContentGenreIsAllowed']),
  TargetingContentID: parseString(['TargetingContentID']),
  TargetingContentIDIsAllowed: parseNumber('TargetingContentIDIsAllowed'),
  TargetingContentNetwork: parseString(['TargetingContentNetwork']),
  TargetingContentNetworkIsAllowed: parseNumber('TargetingContentNetworkIsAllowed'),
  TargetingContentProducer: parseString(['TargetingContentProducer']),
  TargetingContentProducerIsAllowed: parseNumber(['TargetingContentProducerIsAllowed']),
  TargetingContentRating: parseString(['TargetingContentRating']),
  TargetingContentRatingIsAllowed: parseNumber(['TargetingContentRatingIsAllowed']),
  TargetingContentSeries: parseString(['TargetingContentSeries']),
  TargetingContentSeriesIsAllowed: parseNumber(['TargetingContentSeriesIsAllowed']),
  TargetingContentTitle: parseString(['TargetingContentTitle']),
  TargetingContentTitleIsAllowed: parseNumber(['TargetingContentTitleIsAllowed']),
  TargetingContentLanguage: parseString(['TargetingContentLanguage']),
  TargetingContentLanguageIsAllowed: parseNumber(['TargetingContentLanguageIsAllowed']),
  TargetingCountryGroupIDs: parseString(['TargetingCountryGroupIDs']),
  TargetingCountryGroupIDsIsAllowed: parseNumber(['TargetingCountryGroupIDsIsAllowed']),
  TargetingAudienceSegmentIDIsAllowed: parseNumber('TargetingAudienceSegmentIDIsAllowed'),
  TargetingAppBundleID: parseString(['TargetingAppBundleID']),
  TargetingAppBundleIDIsAllowed: parseNumber(['TargetingAppBundleIDIsAllowed']),
  TargetingAppBundleSitePagesLogic: parseNumber(['TargetingAppBundleSitePagesLogic']),
  TargetingDealID: parseString(['TargetingDealID']),
  TargetingDealIDIsAllowed: parseNumber(['TargetingDealIDIsAllowed']),
  TargetingFWSectionIDs: parseString(['TargetingFWSectionIDs']),
  TargetingFWSectionIDsIsAllowed: parseNumber(['TargetingFWSectionIDsIsAllowed']),
  TargetingLivestream: parseNumber(['TargetingLivestream']),
  LocalTargetingAllowed: parseString(['LocalTargetingAllowed']),
  LocalTargetingDisallowed: parseString(['LocalTargetingDisallowed']),
  CustomTargeting: parseString(['CustomTargeting']),
  CustomTargetingInclusion: parseNumber(['CustomTargetingInclusion']),
  TargetingCustom4: parseString(['TargetingCustom4']),
  TargetingCustom4Inclusion: parseNumber(['TargetingCustom4Inclusion']),
  TargetingCustom5: parseString(['TargetingCustom5']),
  TargetingCustom5Inclusion: parseNumber(['TargetingCustom5Inclusion']),
  TargetingCustom6: parseString(['TargetingCustom6']),
  TargetingCustom6Inclusion: parseNumber(['TargetingCustom6Inclusion']),
  TargetingCustom7: parseString(['TargetingCustom7']),
  TargetingCustom7Inclusion: parseNumber(['TargetingCustom7Inclusion']),
  TargetingCustom8: parseString(['TargetingCustom8']),
  TargetingCustom8Inclusion: parseNumber(['TargetingCustom8Inclusion']),
  TargetingCustom9: parseString(['TargetingCustom9']),
  TargetingCustom9Inclusion: parseNumber(['TargetingCustom9Inclusion']),
  TargetingCustom10: parseString(['TargetingCustom10']),
  TargetingCustom10Inclusion: parseNumber(['TargetingCustom10Inclusion']),
  TargetingCustom11: parseString(['TargetingCustom11']),
  TargetingCustom11Inclusion: parseNumber(['TargetingCustom11Inclusion']),
  TargetingCustom12: parseString(['TargetingCustom12']),
  TargetingCustom12Inclusion: parseNumber(['TargetingCustom12Inclusion']),
  TargetingCustom13: parseString(['TargetingCustom13']),
  TargetingCustom13Inclusion: parseNumber(['TargetingCustom13Inclusion']),
  TargetingCustom14: parseString(['TargetingCustom14']),
  TargetingCustom14Inclusion: parseNumber(['TargetingCustom14Inclusion']),
  TargetingCustom15: parseString(['TargetingCustom15']),
  TargetingCustom15Inclusion: parseNumber(['TargetingCustom15Inclusion']),
  TargetingCustom16: parseString(['TargetingCustom16']),
  TargetingCustom16Inclusion: parseNumber(['TargetingCustom16Inclusion']),
  TargetingCustom17: parseString(['TargetingCustom17']),
  TargetingCustom17Inclusion: parseNumber(['TargetingCustom17Inclusion']),
  TargetingCustom18: parseString(['TargetingCustom18']),
  TargetingCustom18Inclusion: parseNumber(['TargetingCustom18Inclusion']),
  IPFilter: parseString(['IPFilter']),
  PacingType: parseNumber(['PacingType']),
  Dayparting: ['Dayparting'],
  DaypartingIsAllowed: parseNumber(['DaypartingIsAllowed']),
  SegmentTargeting: parseNumber(['SegmentTargeting']),
  ChannelGroupIDS: parseArray(['ChannelGroupIDS', 'channel_group_ids'], (obj: any) => {
    return obj;
  }),
  ChannelListIDS: parseArray(['ChannelListIDS', 'channel_list_ids'], (obj: any) => {
    return obj;
  }),
  ChannelListAdserverCampaignAssociations: parseArray(
    ['ChannelListAdserverCampaignAssociations'],
    transformChannelListAdserverCampaignAssociations,
  ),
  TargetingDeviceTypes: parseString(['TargetingDeviceTypes']),
  TargetingDeviceTypesIsAllowed: parseNumber(['TargetingDeviceTypesIsAllowed']),
  TargetingDeviceSubTypes: parseString(['TargetingDeviceSubTypes']),
  TargetingDeviceSubTypesIsAllowed: parseNumber('TargetingDeviceSubTypesIsAllowed'),
  TargetingDeviceModels: parseString(['TargetingDeviceModels']),
  TargetingDeviceModelsIsAllowed: parseNumber('TargetingDeviceModelsIsAllowed'),
  TargetingCoppa: parseNumber(['TargetingCoppa']),
  TargetingLimitedAdTracking: parseNumber(['TargetingLimitedAdTracking']),
  AdserverOrderID: parseNumber(['AdserverOrderID']),
  SiteAdserverCampaignAssociations: parseArray(
    ['SiteAdserverCampaignAssociations'],
    transformSiteAdserverCampaignAssociations,
  ),
  ChannelGroupAdserverCampaignAssociations: parseArray(
    ['ChannelGroupAdserverCampaignAssociations'],
    transformChannelGroupAdserverCampaignAssociations,
  ),
  ContextualSegmentIDS: parseArray(['ContextualSegmentIDS'], (obj: any) => {
    return obj;
  }),
  SiteWeightsActive: parseNumber(['SiteWeightsActive']),
  DisableLowPacing: parseNumber(['DisableLowPacing']),
  EnableOverDelivery: parseNumber(['EnableOverDelivery']),
  SkipEnabled: parseNumber(['SkipEnabled']),
  SkipOffset: parseNumber(['SkipOffset']),
  LocalTargetingAllowedLogic: parseNumber(['LocalTargetingAllowedLogic']),
  LocalTargetingDisallowedLogic: parseNumber(['LocalTargetingDisallowedLogic']),
  FlightDateCheckpoints: parseArray(['FlightDateCheckpoints'], transformFlightDateCheckpoint),
  FlightDateCheckpointsEnabled: parseNumber(['FlightDateCheckpointsEnabled']),
  FlightDateCheckpointsRolloverEnabled: parseNumber(['FlightDateCheckpointsRolloverEnabled']),
  GuaranteedDeliveryEnabled: parseNumber(['GuaranteedDeliveryEnabled']),
  Alias: parseString(['Alias']),
  TargetingCCPA: ['TargetingCCPA'],
  EnforceSameCreativePerReqEnabled: ['EnforceSameCreativePerReqEnabled'],
  EnableFlightDateSubgroups: ['EnableFlightDateSubgroups'],
  CategoryExclusivityEnabled: ['CategoryExclusivityEnabled'],
  TargetingRequestedPodDurationMin: ['TargetingRequestedPodDurationMin'],
  TargetingRequestedPodDurationMax: ['TargetingRequestedPodDurationMax'],
  TargetingGvinParamExists: ['TargetingGvinParamExists'],
  ServeRightAway: parseNumber(['ServeRightAway']),
  TargetingGDPR: parseNumber(['TargetingGDPR', 'targeting_gdpr']),
  TargetingGDPRConsent: parseNumber(['TargetingGDPRConsent', 'targeting_gdpr_consent']),
  PresetIDs: ['PresetIDs', 'preset_ids'],
  SamplingPercentageDisabledUntil: ['SamplingPercentageDisabledUntil'],
  SamplingPercentage: parseFloat(['SamplingPercentage']),
  TargetingDeviceIDType: parseNumber(['TargetingDeviceIDType']),
  TargetingUID20TargetingType: parseNumber(['TargetingUID20TargetingType']),
  TargetingGooglePairIDTargetingType: parseNumber(['TargetingGooglePairIDTargetingType']),
  TargetingLiveRampATSTargetingType: parseNumber(['TargetingLiveRampATSTargetingType']),
  TargetingIrisSignalTargetingType: parseNumber(['TargetingIrisSignalTargetingType']),
  // TargetingSamsungIdentityTargetingType: parseNumber(['TargetingSamsungIdentityTargetingType']),
  // TargetingSamsungEPGMetadataTargetingType: parseNumber([
  //   'TargetingSamsungEPGMetadataTargetingType',
  // ]),
});
